import React, { useState, useEffect, useCallback } from 'react';
import ProjectNameDate from './steps/ProjectNameDate';
import BudgetHours from './steps/BudgetHours';
import ImplementationType from './steps/ImplementationType';
import PreferredResources from './steps/PreferredResources';
import TechStack from './steps/TechStack';
import SprintMilestoneDeliverables from './steps/SprintMilestoneDeliverables';
import ProjectCosts from './steps/ProjectCosts';
import ProjectReview from './steps/ProjectReview';
import './styles/ProjectWizard.css';
import ProductHeader from "../components/ProductHeader";

const steps = [
    "Project Name & Start Date", "Budget & Hours", "Implementation Type",
    "Preferred Resources", "Tech Stack", "Sprints/Milestones",
    "Project Costs", "Project Review"
];

const ProjectWizard = ({ formData, setFormData, navigateToPage, onGenerateProjectPlan }) => {
    const [currentStep, setCurrentStep] = useState(formData.currentStep || 1);
    const [farthestStep, setFarthestStep] = useState(formData.currentStep || 1);
    const [validationFunctions, setValidationFunctions] = useState({});

    useEffect(() => {
        setFormData(prevData => ({
            ...prevData,
            currentPage: "wizard" // 🔹 Set this to the correct page name
        }));
    }, []);
    
    // ✅ Fix: Ensure `currentStep` updates correctly without causing infinite loops
    useEffect(() => {
        if (formData.currentStep) {
            setCurrentStep(formData.currentStep);
            setFarthestStep(prev => Math.max(prev, formData.currentStep)); // ✅ Prevent unnecessary updates
        }
    }, [formData.currentStep]); // ✅ Only runs when `currentStep` changes

    // ✅ Function to update formData safely
    const updateFormData = useCallback((updates) => {
        setFormData((prevData) => ({ ...prevData, ...updates }));
    }, [setFormData]);

    // ✅ Function to handle step navigation
    const updateStep = (newStep) => {
        setCurrentStep(newStep);
        setFormData((prev) => ({ ...prev, currentStep: newStep }));
    };

    const nextStep = () => {
        const validateCurrentStep = validationFunctions[currentStep];
        if (validateCurrentStep && !validateCurrentStep(true)) return;
        updateStep(Math.min(currentStep + 1, steps.length));
    };

    const prevStep = () => updateStep(Math.max(currentStep - 1, 1));

    // ✅ Prevents unnecessary updates to `setStepValidation`
    const setStepValidation = useCallback((stepNumber, validateFunction) => {
        setValidationFunctions((prev) => {
            if (prev[stepNumber] === validateFunction) return prev; // ✅ Prevents redundant re-renders
            return { ...prev, [stepNumber]: validateFunction };
        });
    }, []);

    // ✅ Renders the correct step
    const renderStep = () => {
        switch (currentStep) {
            case 1: return <ProjectNameDate formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={1} />;
            case 2: return <BudgetHours formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={2} />;
            case 3: return <ImplementationType formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={3} />;
            case 4: return <PreferredResources formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={4} />;
            case 5: return <TechStack formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={5} />;
            case 6: return <SprintMilestoneDeliverables formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={6} />;
            case 7: return <ProjectCosts formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={7} goToStep={updateStep} />;
            case 8: return <ProjectReview formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={8} goToStep={updateStep} onGenerateProjectPlan={() => navigateToPage("summary")}/>;
            default: return <ProjectNameDate formData={formData} updateFormData={updateFormData} setStepValidation={setStepValidation} stepNumber={1} />;
        }
    };

    return (
        <div className="project-wizard-container">
            <div className="wizard-container">
                <ProductHeader navigateToPage={navigateToPage} formData={formData} />

                {/* ✅ Progress Bar */}
                <div className="progress-bar">
                    {steps.map((step, index) => {
                        const stepNumber = index + 1;
                        const isClickable = stepNumber <= farthestStep;
                        return (
                            <div
                                key={index}
                                className={`progress-step ${stepNumber <= currentStep ? 'completed' : ''} ${stepNumber === currentStep ? 'active' : ''}`}
                                onClick={() => isClickable && updateStep(stepNumber)}
                                title={step}
                                style={{ cursor: isClickable ? 'pointer' : 'not-allowed' }}
                            />
                        );
                    })}
                </div>

                {/* ✅ Step Name Display */}
                <div className="progress-bar flex flex-col items-center">
                    <span className="text-lg font-semibold text-blue-800 mb-2">
                        {steps[currentStep - 1]}
                    </span>
                </div>

                {/* ✅ Render Current Step */}
                <div>{renderStep()}</div>

                {/* ✅ Navigation Buttons */}
                <div>
                    <div
                        className="wizard-button-container"
                        style={{
                            justifyContent:
                                currentStep === 1
                                    ? 'flex-end'
                                    : currentStep === steps.length
                                        ? 'flex-start'
                                        : 'space-between',
                        }}
                    >
                        {currentStep > 1 && (
                            <button className="prev-button" onClick={prevStep}>
                                Previous
                            </button>
                        )}
                        {currentStep < steps.length && (
                            <button className="next-button" onClick={nextStep}>
                                Next
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectWizard;
